import { z } from "zod"
import { CAMPAIGN_FAMILIES } from "../campaign-family"
import { marketplaceIdSchema } from "../marketplace-id"
import { isoCalendarDay, isoDate } from "./primitives"

/** Analysis evidence is navigated from the dashboard. Accept only bounded
 * HTTPS URLs even when a producer supplies another syntactically valid scheme. */
function httpsUrlSchema() {
  return z
    .string()
    .url()
    .max(2048)
    .refine((value) => {
      try {
        return new URL(value).protocol === "https:"
      } catch {
        return false
      }
    }, "expected an HTTPS URL")
}

// ── Ad reports (advertiser-report import, #166) ──────────────────────────────

// Report sources that land in marketplace_ad_reports_daily. `redcare` (the
// Redcare advertiser report — Shop Apotheke Italia's CPC export) is the only one
// today; mirrors the backend `AD_REPORT_SOURCES` enum.
export const adReportSourceSchema = z.enum(["redcare"])
export type AdReportSource = z.infer<typeof adReportSourceSchema>

/** The Redcare programme a campaign runs under. Mirrors `CAMPAIGN_FAMILY`,
 *  built straight off it so the wire contract and the classifier cannot drift. */
export const campaignFamilySchema = z.enum(CAMPAIGN_FAMILIES)

// Body of POST /api/marketplaces/:marketplace/ad-report/import. Raw CSV text in
// (the API owns parsing + validation); the original filename is kept as
// provenance (`report_file`).
export const adReportImportBodySchema = z
  .object({
    csv: z.string().min(1),
    reportFile: z.string().min(1),
    // Present only for an automated portal sync. The range is authoritative:
    // dates in it that are absent from the CSV must be cleared as zero-traffic
    // days. Independent-source manual files may omit both bounds and retain
    // per-file-day replacement semantics; shared-account markets refuse that
    // shape because it carries no verified partition boundary.
    from: isoCalendarDay.optional(),
    to: isoCalendarDay.optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if ((value.from === undefined) !== (value.to === undefined)) {
      ctx.addIssue({
        code: "custom",
        message: "from and to must be supplied together",
      })
      return
    }
    if (value.from !== undefined && value.to !== undefined && value.from > value.to) {
      ctx.addIssue({
        code: "custom",
        message: "from must be on or before to",
      })
    }
  })
export type AdReportImportBody = z.infer<typeof adReportImportBodySchema>

/** Bounded fact-read window. Interactive reads may use trailing `days`; jobs
 * pin exact inclusive bounds so every input and the stored analysis agree. */
export const adReportWindowQuerySchema = z
  .object({
    days: z.string().regex(/^\d+$/, "days must be an integer").optional(),
    from: isoCalendarDay.optional(),
    to: isoCalendarDay.optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const hasExactBound = value.from !== undefined || value.to !== undefined
    if ((value.from === undefined) !== (value.to === undefined)) {
      ctx.addIssue({ code: "custom", message: "from and to must be supplied together" })
      return
    }
    if (value.days !== undefined && hasExactBound) {
      ctx.addIssue({ code: "custom", message: "days cannot be combined with from and to" })
      return
    }
    if (value.from !== undefined && value.to !== undefined) {
      if (value.from > value.to) {
        ctx.addIssue({ code: "custom", message: "from must be on or before to" })
        return
      }
      const inclusiveDays =
        Math.floor(
          (Date.parse(`${value.to}T00:00:00.000Z`) - Date.parse(`${value.from}T00:00:00.000Z`)) /
            (24 * 60 * 60 * 1000)
        ) + 1
      if (inclusiveDays > 365) {
        ctx.addIssue({ code: "custom", message: "window must span at most 365 days" })
      }
    }
  })
export type AdReportWindowQuery = z.infer<typeof adReportWindowQuerySchema>

// A row kept with a null product_id because its EAN matched no catalog product.
const adReportUnmatchedEanSchema = z.object({
  line: z.number().int(),
  campaignId: z.string(),
  ean: z.string(),
})

// A matched row whose snapshot cost is zero/placeholder (e.g. €1.00) — its
// eventual PnL would be wrong, so it is surfaced, never dropped.
const adReportPlaceholderCostSchema = z.object({
  line: z.number().int(),
  campaignId: z.string(),
  ean: z.string().nullable(),
  productId: z.string(),
  unitCost: z.number().nullable(),
})

const adReportMalformedRowSchema = z.object({
  line: z.number().int(),
  reason: z.string(),
  raw: z.string(),
})

// Several file lines that collapse to one identity
// (marketplace, source, date, campaign, ean). Kept as last-wins; every
// contributing line is listed so nothing is silently merged.
const adReportDuplicateRowSchema = z.object({
  date: isoDate,
  campaignId: z.string(),
  ean: z.string().nullable(),
  lines: z.array(z.number().int()),
})

// A kept row with a non-empty but unparseable numeric cell (that fact is null).
const adReportNumericIssueRowSchema = z.object({
  line: z.number().int(),
  campaignId: z.string(),
  columns: z.array(z.string()),
})

// ── Marketplace PnL (fee-aware compute, #167) ────────────────────────────────

// A `marketplace_pnl_daily` source: an ad-report source (the PPC slice) or
// `orders` (the whole-marketplace book computed from synced orders). Mirrors
// the backend `PNL_SOURCES` enum.
export const pnlSourceSchema = z.enum([...adReportSourceSchema.options, "orders"])
export type PnlSourceId = z.infer<typeof pnlSourceSchema>

// Body of POST /api/marketplaces/:marketplace/pnl/recompute. All optional: no
// range rebuilds ALL synced/imported history for the source (fee-model version
// bump); a [from, to] range is expanded to whole calendar months on the server.
export const pnlRecomputeBodySchema = z
  .object({
    source: pnlSourceSchema.optional(),
    from: isoDate.optional(),
    to: isoDate.optional(),
  })
  .strict()
export type PnlRecomputeBody = z.infer<typeof pnlRecomputeBodySchema>

// One day of ad facts, aggregated. `roas` is derived (gmv/spend) — the row-level
// platform roas can't be summed. Numerics arrive as numbers (the API converts
// the DB's numeric-as-string columns at the module boundary).
export const adReportHistoryDaySchema = z.object({
  date: isoDate,
  impressions: z.number(),
  clicks: z.number(),
  conversions: z.number(),
  spend: z.number(),
  gmv: z.number(),
  roas: z.number(),
})

// GET /api/marketplaces/:marketplace/ad-report/history — imported ad
// facts pre-grouped per campaign (per-day series + window totals), plus a
// whole-portfolio per-day series. The read surface `nova ads history` renders
// and the ads-review agent reasons over; sorted by window spend, biggest first.
export const adReportHistoryResponseSchema = z.object({
  marketplace: marketplaceIdSchema,
  reportSource: adReportSourceSchema,
  from: isoDate,
  to: isoDate,
  totalsByDay: z.array(adReportHistoryDaySchema.extend({ activeCampaigns: z.number().int() })),
  // Latest successful authoritative portal import. Its coverage distinguishes
  // a genuinely quiet day from a sync that never completed; its persisted
  // flags let the weekly analysis surface import-quality work.
  latestSync: z
    .object({
      importedAt: isoDate,
      reportFile: z.string(),
      from: isoDate,
      to: isoDate,
      rowsInFile: z.number().int(),
      rowsWritten: z.number().int(),
      matchedRows: z.number().int(),
      noEanRows: z.number().int(),
      currencyMismatch: z.boolean(),
      flags: z.object({
        unmatchedEans: z.array(adReportUnmatchedEanSchema),
        placeholderCosts: z.array(adReportPlaceholderCostSchema),
        malformedRows: z.array(adReportMalformedRowSchema),
        duplicateRows: z.array(adReportDuplicateRowSchema),
        numericIssueRows: z.array(adReportNumericIssueRowSchema),
      }),
    })
    .nullable(),
  campaigns: z.array(
    z.object({
      campaignId: z.string(),
      campaignName: z.string().nullable(),
      /** The Redcare programme, read off `campaignName` (#574) — so a reader of
       *  the trend never has to know the prefix rule itself. */
      family: campaignFamilySchema.nullable(),
      products: z.array(
        z.object({
          ean: z.string().nullable(),
          productId: z.string().nullable(),
        })
      ),
      totals: adReportHistoryDaySchema.omit({ date: true }),
      days: z.array(adReportHistoryDaySchema),
    })
  ),
})
export type AdReportHistoryReport = z.infer<typeof adReportHistoryResponseSchema>

export const adReportImportResponseSchema = z.object({
  marketplace: marketplaceIdSchema,
  reportSource: adReportSourceSchema,
  reportFile: z.string(),
  // Snapshot store code and the marketplace's currency (derived, not in the
  // CSV). v1 targets EUR; `currencyMismatch` flags a non-EUR marketplace.
  store: z.string().nullable(),
  currency: z.string().nullable(),
  currencyExpected: z.string(),
  currencyMismatch: z.boolean(),
  authoritativeRange: z.object({ from: isoDate, to: isoDate }).nullable(),
  // Distinct report days written (idempotency is per day).
  dates: z.array(isoDate),
  rowsInFile: z.number().int(),
  rowsWritten: z.number().int(),
  matchedRows: z.number().int(),
  // Informational: rows with no EAN at all (non-product overhead lines).
  noEanRows: z.number().int(),
  flags: z.object({
    unmatchedEans: z.array(adReportUnmatchedEanSchema),
    placeholderCosts: z.array(adReportPlaceholderCostSchema),
    malformedRows: z.array(adReportMalformedRowSchema),
    duplicateRows: z.array(adReportDuplicateRowSchema),
    numericIssueRows: z.array(adReportNumericIssueRowSchema),
  }),
})
export type AdReportImportReport = z.infer<typeof adReportImportResponseSchema>

// ── Ad profitability ───────────────────────────────────────────────────────
// The deterministic bid/close verdict derived from imported ad facts. Every
// number here is computed from `marketplace_ad_reports_daily` — the per-unit
// margin snapshot (`real_income`, `unit_price`) plus the window's own traffic —
// and the marketplace's `ads.policy`. No live portal read is involved.

export const adProfitabilityActionSchema = z.enum([
  "close",
  "lower-bid",
  "keep",
  "raise-bid",
  "watch",
])
export type AdProfitabilityAction = z.infer<typeof adProfitabilityActionSchema>

/** Whether the product's margin can carry ads at all. `unknown` is NOT
 *  `unprofitable`: it means no margin snapshot was resolvable (unmatched EAN,
 *  missing cost, or a marketplace with no fee/ads config), so no verdict about
 *  the product's economics can honestly be reached. */
export const adProfitabilityViabilitySchema = z.enum(["viable", "unprofitable", "unknown"])
export type AdProfitabilityViability = z.infer<typeof adProfitabilityViabilitySchema>

// One campaign × product line over the window. `ctr`/`cvr`/`roas` are DERIVED
// from the summed facts — the stored per-row platform values cannot be summed.
export const adProfitabilityLineSchema = z.object({
  campaignId: z.string(),
  campaignName: z.string().nullable(),
  campaignType: z.string().nullable(),
  /** Which Redcare programme the campaign belongs to, read off `campaignName`.
   *  `campaignType` is `cpc` on both programmes, so it cannot say (#574). Null
   *  when the name does not say. */
  family: campaignFamilySchema.nullable(),
  ean: z.string().nullable(),
  productId: z.string().nullable(),
  /** Report days in the window that carried facts for this line. */
  days: z.number().int(),
  impressions: z.number(),
  clicks: z.number(),
  conversions: z.number(),
  spend: z.number(),
  /** VAT-INCLUSIVE, summed as the advertiser report sent it. */
  gmv: z.number(),
  /** `gmv` with each fact's own VAT stripped before summing (#577). `roas` is
   *  derived from THIS, so a reader can reproduce the ratio off the line. */
  netGmv: z.number(),
  ctr: z.number(),
  cvr: z.number(),
  /** `netGmv / spend` — net of VAT, the same basis as `breakEvenRoas`. */
  roas: z.number(),
  // Margin snapshot, taken from the latest day in the window that carried one.
  /** GROSS (VAT-inclusive) catalog price — the commission base. */
  unitPrice: z.number().nullable(),
  /** The same price net of that day's own VAT — what `breakEvenRoas` stands on. */
  netUnitPrice: z.number().nullable(),
  realIncome: z.number().nullable(),
  breakEvenRoas: z.number().nullable(),
  targetRoas: z.number().nullable(),
  /** The campaign's bid, from the latest day in the window that carried one.
   *  Null when no day did — a day whose source value said nothing (blank, or a
   *  cost-per-click of 0 because there were no clicks) is stored as unknown. */
  bid: z.number().nullable(),
  /** Highest CPC that still breaks even, at this line's own CVR. */
  breakEvenBid: z.number().nullable(),
  /** The bid a recommendation moves to: the target-ROAS bid at this line's CVR,
   *  held down to `policy.maxBidShare` × margin. Already capped — a reader never
   *  has to apply the ceiling themselves. */
  targetBid: z.number().nullable(),
  viability: adProfitabilityViabilitySchema,
  action: adProfitabilityActionSchema,
  /** The bid to move to, when the action is a bid change. */
  newBid: z.number().nullable(),
  /** Real € contributed over the window: conversions × realIncome − spend. Null
   *  only when the margin is unknown — a loss IS reported, not hidden. */
  netProfit: z.number().nullable(),
  reason: z.string(),
})
export type AdProfitabilityLine = z.infer<typeof adProfitabilityLineSchema>

// GET /api/marketplaces/:marketplace/ad-report/profitability?days=N
export const adProfitabilityResponseSchema = z.object({
  marketplace: marketplaceIdSchema,
  reportSource: adReportSourceSchema,
  from: isoDate,
  to: isoDate,
  // Echoed so a recommendation can be read back against the policy that
  // produced it — the thresholds are config, not constants in the code.
  policy: z.object({
    targetProfitShare: z.number(),
    /** Ceiling on every recommended bid, as a share of the per-unit margin — so a
     *  reader can check `newBid` against it without knowing the config. */
    maxBidShare: z.number(),
    /** Lowest bid a new CPC edit may create in the portal. */
    minEditableBid: z.number(),
    volumeGateMultiple: z.number(),
  }),
  // `spend`/`gmv` cover EVERY line; `netProfit` only those with a computable
  // margin, so an unmeasurable line can never quietly shrink the portfolio's
  // cost. `marginPct` is netProfit over the measured lines' gmv.
  totals: z.object({
    lines: z.number().int(),
    spend: z.number(),
    gmv: z.number(),
    netProfit: z.number(),
    marginPct: z.number().nullable(),
  }),
  /** The lines excluded from `netProfit` — margin unknown. Reported rather than
   *  dropped so the operator can see how much spend is unaccounted for. */
  unmeasured: z.object({
    lines: z.number().int(),
    spend: z.number(),
    gmv: z.number(),
  }),
  /** The same roll-up cut by Redcare programme, biggest spender first (#574).
   *  Pooling the two describes neither: on Shop Apotheke IT the whole ad loss
   *  sits in Search SPA while cross-sell earns on a few percent of the spend.
   *  Only families the window actually holds get an entry — no invented zeroes.
   *  `unmeasuredLines` carries the same caveat `unmeasured` does for the
   *  portfolio: that many of the family's lines have no computable margin, so its
   *  `netProfit` covers the rest. */
  byFamily: z.array(
    z.object({
      family: campaignFamilySchema.nullable(),
      lines: z.number().int(),
      unmeasuredLines: z.number().int(),
      spend: z.number(),
      gmv: z.number(),
      netProfit: z.number(),
      marginPct: z.number().nullable(),
    })
  ),
  // Biggest spender first — the lines worth reading about come up top.
  lines: z.array(adProfitabilityLineSchema),
})
export type AdProfitabilityReport = z.infer<typeof adProfitabilityResponseSchema>

// ── Immutable campaign analysis runs ───────────────────────────────────────

export const adAnalysisDecisionSchema = z.enum([
  "hold",
  "watch",
  "pause",
  "activate",
  "raise-bid",
  "lower-bid",
  "data-check",
  "retest",
  "propose",
])
export type AdAnalysisDecision = z.infer<typeof adAnalysisDecisionSchema>

export const adAnalysisPrioritySchema = z.enum(["high", "medium", "low"])
export type AdAnalysisPriority = z.infer<typeof adAnalysisPrioritySchema>

export const adAnalysisSubjectKindSchema = z.enum(["existing-campaign", "new-campaign-candidate"])
export type AdAnalysisSubjectKind = z.infer<typeof adAnalysisSubjectKindSchema>

export const adCampaignMembershipStatusSchema = z.enum(["complete", "unavailable"])
export type AdCampaignMembershipStatus = z.infer<typeof adCampaignMembershipStatusSchema>

const adAnalysisPlanSchema = z.object({
  decision: adAnalysisDecisionSchema,
  priority: adAnalysisPrioritySchema,
  suggestedBid: z.number().positive().nullable(),
  reasoning: z.string().trim().min(20).max(4000),
  nextStep: z.string().trim().min(1).max(1000),
  sourceUrl: httpsUrlSchema().nullable(),
})

export const adAnalysisRowInputSchema = z
  .discriminatedUnion("subjectKind", [
    adAnalysisPlanSchema
      .extend({
        subjectKind: z.literal("existing-campaign"),
        campaignId: z.string().min(1),
        ean: z.string().min(1).nullable(),
      })
      .strict(),
    adAnalysisPlanSchema
      .extend({
        subjectKind: z.literal("new-campaign-candidate"),
        productId: z.string().min(1),
      })
      .strict(),
  ])
  .superRefine((value, context) => {
    if (value.subjectKind === "new-campaign-candidate" && value.suggestedBid !== null) {
      context.addIssue({
        code: "custom",
        path: ["suggestedBid"],
        message: "new-campaign candidates use the server-owned EUR 0.35 initial bid",
      })
    }
  })
export type AdAnalysisRowInput = z.infer<typeof adAnalysisRowInputSchema>

export const adCampaignSnapshotSchema = z
  .object({
    capturedAt: z.string().datetime({ offset: true }),
    membershipStatus: adCampaignMembershipStatusSchema,
    campaigns: z
      .array(
        z
          .object({
            campaignId: z.string().min(1),
            campaignType: z.string().min(1).nullable(),
            campaignName: z.string().min(1).nullable(),
            productEans: z.array(z.string().min(1)).max(5000),
            currentBid: z.number().nonnegative(),
            isActive: z.boolean(),
            soldOut: z.boolean(),
          })
          .strict()
      )
      .max(5000),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (
      new Set(value.campaigns.map((campaign) => campaign.campaignId)).size !==
      value.campaigns.length
    ) {
      ctx.addIssue({
        code: "custom",
        message: "campaignIds must be unique in the complete snapshot",
      })
    }
    value.campaigns.forEach((campaign, index) => {
      if (new Set(campaign.productEans).size !== campaign.productEans.length) {
        ctx.addIssue({
          code: "custom",
          path: ["campaigns", index, "productEans"],
          message: "productEans must be unique within one campaign",
        })
      }
    })
    if (
      value.membershipStatus === "unavailable" &&
      value.campaigns.some((campaign) => campaign.productEans.length > 0)
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["campaigns"],
        message: "productEans must be empty when current campaign membership is unavailable",
      })
    }
  })
export type AdCampaignSnapshot = z.infer<typeof adCampaignSnapshotSchema>

const adAnalysisAttemptFields = {
  deliveryId: z.string().min(1).max(200),
  window: z.object({ from: isoCalendarDay, to: isoCalendarDay }).strict(),
  agent: z
    .object({
      provider: z.string().min(1).max(100),
      model: z.string().min(1).max(200),
      promptVersion: z.string().min(1).max(200),
    })
    .strict(),
} as const

const adAnalysisSubmissionFields = {
  ...adAnalysisAttemptFields,
  rows: z.array(adAnalysisRowInputSchema).min(1).max(500),
} as const

function checkAnalysisWindow(
  value: { window: { from: string; to: string } },
  ctx: z.RefinementCtx
): void {
  if (value.window.from > value.window.to) {
    ctx.addIssue({ code: "custom", message: "window.from must be on or before window.to" })
  }
}

export const adAnalysisSubmissionSchema = z
  .object(adAnalysisSubmissionFields)
  .strict()
  .superRefine(checkAnalysisWindow)
export type AdAnalysisSubmission = z.infer<typeof adAnalysisSubmissionSchema>

export const adAnalysisAttemptSchema = z
  .object(adAnalysisAttemptFields)
  .strict()
  .superRefine(checkAnalysisWindow)
export type AdAnalysisAttempt = z.infer<typeof adAnalysisAttemptSchema>

/** Internal deterministic wire body. The agent-authored file is only an
 * `AdAnalysisSubmission`; the CLI obtains this snapshot directly from Redcare
 * after reading that file. */
export const adAnalysisBodySchema = z
  .object({ ...adAnalysisSubmissionFields, campaignSnapshot: adCampaignSnapshotSchema })
  .strict()
  .superRefine(checkAnalysisWindow)
export type AdAnalysisBody = z.infer<typeof adAnalysisBodySchema>

export const adAnalysisWriteBodySchema = z.discriminatedUnion("phase", [
  z
    .object({ ...adAnalysisAttemptFields, phase: z.literal("start") })
    .strict()
    .superRefine(checkAnalysisWindow),
  z
    .object({
      ...adAnalysisSubmissionFields,
      phase: z.literal("complete"),
      campaignSnapshot: adCampaignSnapshotSchema,
    })
    .strict()
    .superRefine(checkAnalysisWindow),
  z
    .object({
      ...adAnalysisAttemptFields,
      phase: z.literal("source-failed"),
      sourceFailure: z.string().trim().min(1).max(1000),
    })
    .strict()
    .superRefine(checkAnalysisWindow),
])
export type AdAnalysisWriteBody = z.infer<typeof adAnalysisWriteBodySchema>

export const adAnalysisGateResultSchema = z.enum(["aligned", "departure", "candidate"])
export type AdAnalysisGateResult = z.infer<typeof adAnalysisGateResultSchema>
export const adAnalysisRunStatusSchema = z.enum(["running", "completed", "failed"])

export const adCampaignMembershipResolutionSchema = z.enum([
  "resolved",
  "unavailable",
  "missing-ean",
  "catalog-not-found",
  "catalog-ambiguous",
])
export type AdCampaignMembershipResolution = z.infer<typeof adCampaignMembershipResolutionSchema>

export const adCampaignMembershipSchema = z.object({
  campaignPosition: z.number().int().nonnegative(),
  productPosition: z.number().int().nonnegative(),
  campaignId: z.string(),
  campaignType: z.string().nullable(),
  campaignName: z.string().nullable(),
  productName: z.string().nullable(),
  ean: z.string().nullable(),
  productId: z.string().nullable(),
  currentBid: z.number(),
  isActive: z.boolean(),
  soldOut: z.boolean(),
  resolution: adCampaignMembershipResolutionSchema,
})
export type AdCampaignMembership = z.infer<typeof adCampaignMembershipSchema>

export const adAnalysisStoredRowSchema = z.object({
  subjectKind: adAnalysisSubjectKindSchema,
  campaignId: z.string().nullable(),
  productId: z.string().nullable(),
  productName: z.string().nullable(),
  ean: z.string().nullable(),
  decision: adAnalysisDecisionSchema,
  priority: adAnalysisPrioritySchema,
  suggestedBid: z.number().nullable(),
  reasoning: z.string(),
  nextStep: z.string(),
  sourceUrl: z.string().nullable(),
  currentBid: z.number().nullable(),
  clicks: z.number().nullable(),
  conversions: z.number().nullable(),
  spend: z.number().nullable(),
  gmv: z.number().nullable(),
  roas: z.number().nullable(),
  breakEvenRoas: z.number().nullable(),
  targetRoas: z.number().nullable(),
  applicableBidCeiling: z.number().nullable(),
  engineAction: adProfitabilityActionSchema.nullable(),
  engineBid: z.number().nullable(),
  gateResult: adAnalysisGateResultSchema,
  gateReasons: z.array(z.string()),
  organicUnits: z.number().nullable(),
  organicRevenue: z.number().nullable(),
  organicOrders: z.number().int().nullable(),
  unitMargin: z.number().nullable(),
  marginPool: z.number().nullable(),
})
export type AdAnalysisStoredRow = z.infer<typeof adAnalysisStoredRowSchema>

export const adAnalysisRunSchema = z.object({
  id: z.string().uuid(),
  marketplace: marketplaceIdSchema,
  reportSource: adReportSourceSchema,
  deliveryId: z.string(),
  from: isoDate,
  to: isoDate,
  status: adAnalysisRunStatusSchema,
  sourceCoverage: z.object({
    from: isoDate.nullable(),
    to: isoDate.nullable(),
    importedAt: z.string().nullable(),
  }),
  campaignSnapshotAt: z.string(),
  campaignCount: z.number().int(),
  membershipCount: z.number().int(),
  membershipUnresolvedCount: z.number().int(),
  agent: z.object({ provider: z.string(), model: z.string(), promptVersion: z.string() }),
  failureEvidence: z.array(z.string()),
  skippedEvidence: z.array(z.string()),
  createdBy: z.string(),
  createdAt: z.string(),
  completedAt: z.string().nullable(),
  campaignMemberships: z.array(adCampaignMembershipSchema),
  rows: z.array(adAnalysisStoredRowSchema),
})
export type AdAnalysisRun = z.infer<typeof adAnalysisRunSchema>

export const adAnalysisWriteResponseSchema = z.object({
  run: adAnalysisRunSchema,
  replayed: z.boolean(),
  written: z.number().int(),
  gateOutcomes: z.object({
    aligned: z.number().int(),
    departures: z.number().int(),
    candidates: z.number().int(),
  }),
  refusals: z.array(z.string()),
})
export type AdAnalysisWriteReport = z.infer<typeof adAnalysisWriteResponseSchema>

export const adAnalysisReadResponseSchema = z.object({
  latestSuccessful: adAnalysisRunSchema.nullable(),
  latestAttempt: adAnalysisRunSchema.nullable(),
})
export type AdAnalysisReadReport = z.infer<typeof adAnalysisReadResponseSchema>

// One row of GET /api/products/commercials — store-scoped price/cost/vat by
// EAN, the deterministic input for per-product ad economics (`nova ads
// report`). Owned here so the API repo and the CLI share one wire contract.
export const productCommercialsRowSchema = z.object({
  ean: z.string(),
  simpleSku: z.string(),
  price: z.number().nullable(),
  cost: z.number().nullable(),
  // VAT rate as a percentage (e.g. 10 = 10 %), resolved at sync time.
  vat: z.number().nullable(),
  // Gross package weight in kg. Missing/unsupported weights cannot produce ad economics.
  weightKg: z.number().nullable(),
})
export type ProductCommercialsRow = z.infer<typeof productCommercialsRowSchema>
